import { showSelectedItems } from '../service/projects'

export interface SelectedProjectItem {
  id: string
  name: string
  original_codes: string
  cycles: any[]
  grades: any[]
  breadcrumb: string
  lv1_name: string
  lv1_id?: string
  lv2_name?: string
  lv2_id?: string
  lv3_name?: string
  lv3_id?: string
  color: string
  curriculum_kind: string
  curriculum_name: string
  curriculum_id: string
}

export interface SelectedProjectItems {
  contents: SelectedProjectItem[]
  specific_skills: SelectedProjectItem[]
}

/**
 * Busca os items curriculares selecionados no Step 3
 */
export async function fetchSelectedProjectItems(
  projectId: string
): Promise<SelectedProjectItems> {
  try {
    const response = await showSelectedItems(projectId)

    if (!response) {
      return {
        contents: [],
        specific_skills: [],
      }
    }

    // A API retorna os items em um formato específico
    // ⚠️ IMPORTANTE: A API retorna em camelCase (specificSkills, curriculumKind)
    const allContents = response.contents || response.project_lv4_items_contents || []
    const allSpecificSkills = response.specificSkills || response.specific_skills || []

    // Separar items baseado no curriculumKind (camelCase)
    const contents = allContents.filter((item: any) => item.curriculumKind === 'content')
    const skills = allSpecificSkills.filter((item: any) => item.curriculumKind === 'skill')

    // Se specific_skills veio vazio, tentar separar de contents
    if (allSpecificSkills.length === 0 && allContents.length > 0) {
      const separated = allContents.reduce(
        (acc: any, item: any) => {
          const curriculumKind = item.curriculumKind || item.curriculum_kind
          const isSkill = curriculumKind === 'skill'

          if (isSkill) {
            acc.skills.push(item)
          } else {
            acc.contents.push(item)
          }
          return acc
        },
        { contents: [], skills: [] }
      )

      const formattedContents = separated.contents.map((item: any) => formatProjectItem(item, 'content'))
      const formattedSkills = separated.skills.map((item: any) => formatProjectItem(item, 'skill'))

      return {
        contents: formattedContents,
        specific_skills: formattedSkills,
      }
    }

    const formattedContents = contents.map((item: any) => formatProjectItem(item, 'content'))
    const formattedSkills = skills.map((item: any) => formatProjectItem(item, 'skill'))

    return {
      contents: formattedContents,
      specific_skills: formattedSkills,
    }
  } catch (error) {
    console.error('Erro ao buscar items selecionados:', error)
    return {
      contents: [],
      specific_skills: [],
    }
  }
}

/**
 * Formata um item do projeto para o formato esperado pela API de IA
 * Garante que todos os campos estão no formato snake_case
 * ⚠️ IMPORTANTE: Aceita tanto camelCase quanto snake_case da API
 */
function formatProjectItem(item: any, curriculumKind: 'content' | 'skill'): SelectedProjectItem {
  // Extrair valores com fallback para diferentes formatos (camelCase e snake_case)
  const id = item.id || item.lv4ItemId || item.lv4_item_id || ''
  const name = item.name || item.title || ''
  const originalCodes = item.originalCodes || item.original_codes || item.code || ''
  const cycles = Array.isArray(item.cycles) ? item.cycles : []
  const grades = Array.isArray(item.grades) ? item.grades : []
  const breadcrumb = item.breadcrumb || ''

  const lv1Name = item.lv1Name || item.lv1_name || item.lv1_title || ''
  const lv1Id = item.lv1Id || item.lv1_id || undefined
  const lv2Name = item.lv2Name || item.lv2_name || item.lv2_title || ''
  const lv2Id = item.lv2Id || item.lv2_id || undefined
  const lv3Name = item.lv3Name || item.lv3_name || item.lv3_title || ''
  const lv3Id = item.lv3Id || item.lv3_id || undefined

  const color = item.color || '#FF9900'

  // curriculumKind pode vir do item (camelCase ou snake_case) ou ser o parâmetro passado
  const actualCurriculumKind = item.curriculumKind || item.curriculum_kind || curriculumKind

  const curriculumName = item.curriculumName || item.curriculum_name || item.curriculum?.name || ''
  const curriculumId = item.curriculumId || item.curriculum_id || item.curriculum?.id || ''

  return {
    id,
    name,
    original_codes: originalCodes,
    cycles,
    grades,
    breadcrumb,
    lv1_name: lv1Name,
    lv1_id: lv1Id,
    lv2_name: lv2Name,
    lv2_id: lv2Id,
    lv3_name: lv3Name,
    lv3_id: lv3Id,
    color,
    curriculum_kind: actualCurriculumKind,
    curriculum_name: curriculumName,
    curriculum_id: curriculumId,
  }
}

/**
 * Valida se há items selecionados
 */
export function hasSelectedItems(items: SelectedProjectItems): boolean {
  return items.contents.length > 0 || items.specific_skills.length > 0
}
